export interface DashboardDto {
  version: number
  Title: string
  Groups?: DashboardGroupDto<ResultChartDto>[]
  CompareResults?: {
    Groups: DashboardGroupDto<CompareResultsChartDto>[]
  }
}

export interface DashboardGroupDto<TChart extends ResultChartDto | CompareResultsChartDto> {
  Name: string
  Charts: TChart[]
  Layout?: ChartGroupLayout[]
}

export interface ChartGroupLayout {
  Section?: string // if missing, treat it as layout for charts not assigned to any section
  Height: number // vh
  NumberOfColumns?: number // default 100
  NumberOfRows?: number // defauolt 100
}

export type DashboardChartDto =
  | DashboardTimeSeriesChartDto
  | DashboardBarChartDto
  | DashboardBarCategoricalChartDto
  | DashboardScatterPlotChartDto
  | DashboardProbabilityDensityChartDto
  | DashboardAggregateChartDto
  | DashboardThresholdChartDto
  | DashboardTableChartDto

export type ResultChartDto =
  | ResultDashboardTimeSeriesChartDto
  | ResultDashboardBarChartDto
  | ResultDashboardScatterPlotChartDto
  | ResultDashboardProbabilityDensityChartDto
  | ResultDashboardAggregateChartDto
  | ResultDashboardTableChartDto

export type CompareResultsChartDto =
  | CompareDashboardTimeSeriesChartDto
  | CompareDashboardBarChartDto
  | CompareDashboardScatterPlotChartDto
  | CompareDashboardProbabilityDensityChartDto
  | CompareDashboardAggregateChartDto
  | CompareDashboardTableChartDto

export interface DashboardChartBase<
  TNodeSelectItem extends DashboardChartOptionDto = DashboardChartOptionDto,
> {
  // not part of the input yaml - allows to identify specific chart for view
  // interoperability between full screen view and regular view
  uid: string
  // type-guard, the same charts can be used in different context, this is type guard to let us distinguish them
  isCompareResultsChart: boolean

  Type: DashboardChartTypeDto
  LegendPosition?: "top" | "bottom" // defaults to top
  Section?: string
  Title: string
  Description?: DashboardChartDescriptionDto
  Nodes?: string[]
  NodeSelect?: TNodeSelectItem[]
  FillColumns?: `${number}-${number}` // required if chart is part of layout
  FillRows?: `${number}-${number}` // required if chart is part of layout
}

export interface DashboardChartDescriptionDto {
  Title: string
  Content: string[]
}

export interface DashboardChartOptionDto {
  Option: string
  Nodes: string[]
}

export interface AggregateChartNodeSelectItem extends DashboardChartOptionDto {
  Aggregate: DashboardChartAggregateDto[]
}

// these properties of "NodeSelect" should be applicable only for single run result charts
export interface ResultBarChartNodeSelectItem extends DashboardChartOptionDto {
  Compare?: string
  Stacked?: boolean
  ShowAsPercentage?: boolean
}

// Time series chart related types - base + (single run chart / compare runs chart)
export interface BaseDashboardTimeSeriesChartDto extends DashboardChartBase {
  Type: DashboardChartTypeDto.TimeSeries
  Aggregate?: DashboardChartAggregateDto.mean | DashboardChartAggregateDto.median
  ShowAsTable?: boolean
  ShowStandardDeviations?: boolean
}

export interface CompareDashboardTimeSeriesChartDto extends BaseDashboardTimeSeriesChartDto {
  isCompareResultsChart: true
}

export interface ResultDashboardTimeSeriesChartDto extends BaseDashboardTimeSeriesChartDto {
  isCompareResultsChart: false
}

export type DashboardTimeSeriesChartDto =
  | CompareDashboardTimeSeriesChartDto
  | ResultDashboardTimeSeriesChartDto

// Bar chart related types - base + (single run chart / compare runs chart)
export interface BaseDashboardBarChartDto<
  TNodeSelectItem extends DashboardChartOptionDto = DashboardChartOptionDto,
> extends DashboardChartBase<TNodeSelectItem> {
  Type: DashboardChartTypeDto.BarChart
  Aggregate?: DashboardChartAggregateDto.mean | DashboardChartAggregateDto.median
  ShowAsTable?: boolean
  ShowStandardDeviations?: boolean
}

export interface CompareDashboardBarChartDto extends BaseDashboardBarChartDto {
  isCompareResultsChart: true
}

export interface ResultDashboardBarChartDto
  extends BaseDashboardBarChartDto<ResultBarChartNodeSelectItem> {
  isCompareResultsChart: false
  Compare?: string
  Stacked?: boolean
  ShowAsPercentage?: boolean
}

export type DashboardBarChartDto = CompareDashboardBarChartDto | ResultDashboardBarChartDto

// Bar categorical chart related types - base + (single run chart / compare runs chart)
export interface BaseDashboardBarCategoricalChartDto extends DashboardChartBase {
  Type: DashboardChartTypeDto.BarCategoricalChart
  ShowAsTable?: boolean
  Stacked?: boolean
  ShowAsPercentage?: boolean
}

export interface CompareDashboardBarCategoricalChartDto
  extends BaseDashboardBarCategoricalChartDto {
  isCompareResultsChart: true
}

export interface ResultDashboardBarCategoricalChartDto extends BaseDashboardBarCategoricalChartDto {
  isCompareResultsChart: false
}

export type DashboardBarCategoricalChartDto =
  | CompareDashboardBarCategoricalChartDto
  | ResultDashboardBarCategoricalChartDto

// Scatter plot chart related types - base + (single run chart / compare runs chart)
export interface BaseDashboardScatterPlotChartDto extends DashboardChartBase {
  Type: DashboardChartTypeDto.ScatterPlot
}

export interface CompareDashboardScatterPlotChartDto extends BaseDashboardScatterPlotChartDto {
  isCompareResultsChart: true
}

export interface ResultDashboardScatterPlotChartDto extends BaseDashboardScatterPlotChartDto {
  isCompareResultsChart: false
}

export type DashboardScatterPlotChartDto =
  | CompareDashboardScatterPlotChartDto
  | ResultDashboardScatterPlotChartDto

// Probability density chart related types - base + (single run chart / compare runs chart)
export interface BaseDashboardProbabilityDensityChartDto extends DashboardChartBase {
  Type: DashboardChartTypeDto.ProbabilityDensity
}

export interface CompareDashboardProbabilityDensityChartDto
  extends BaseDashboardProbabilityDensityChartDto {
  isCompareResultsChart: true
}

export interface ResultDashboardProbabilityDensityChartDto
  extends BaseDashboardProbabilityDensityChartDto {
  isCompareResultsChart: false
}

export type DashboardProbabilityDensityChartDto =
  | CompareDashboardProbabilityDensityChartDto
  | ResultDashboardProbabilityDensityChartDto

// Aggregate chart related types - base + (single run chart / compare runs chart)
export interface BaseDashboardAggregateChartDto
  extends DashboardChartBase<AggregateChartNodeSelectItem> {
  Type: DashboardChartTypeDto.Aggregate
  Aggregate: DashboardChartAggregateDto[]
}

export interface CompareDashboardAggregateChartDto extends BaseDashboardAggregateChartDto {
  isCompareResultsChart: true
}

export interface ResultDashboardAggregateChartDto extends BaseDashboardAggregateChartDto {
  isCompareResultsChart: false
}

export type DashboardAggregateChartDto =
  | CompareDashboardAggregateChartDto
  | ResultDashboardAggregateChartDto

export interface DashboardThresholdChartDto extends DashboardChartBase {
  Type: DashboardChartTypeDto.Threshold
  Default_event_node?: string
  Default_given_event_node?: string
}

// Table chart related types - base + (single run chart / compare runs chart)
export interface BaseDashboardTableChartDto extends DashboardChartBase {
  Type: DashboardChartTypeDto.Table
}

export interface CompareDashboardTableChartDto extends BaseDashboardTableChartDto {
  isCompareResultsChart: true
}

export interface ResultDashboardTableChartDto extends BaseDashboardTableChartDto {
  isCompareResultsChart: false
}

export type DashboardTableChartDto = CompareDashboardTableChartDto | ResultDashboardTableChartDto

export enum DashboardChartTypeDto {
  TimeSeries = "TimeSeries",
  BarChart = "BarChart",
  BarCategoricalChart = "BarCategoricalChart",
  ScatterPlot = "ScatterPlot",
  ProbabilityDensity = "ProbabilityDensity",
  Aggregate = "Aggregate",
  Threshold = "Threshold",
  Table = "Table",
}

export enum DashboardChartAggregateDto {
  mean = "mean",
  std = "std",
  median = "median",
  max = "max",
  min = "min",
  "5th_quantile" = "5th_quantile",
  "10th_quantile" = "10th_quantile",
  "25th_quantile" = "25th_quantile",
  "50th_quantile" = "50th_quantile",
  "75th_quantile" = "75th_quantile",
  "90th_quantile" = "90th_quantile",
  "95th_quantile" = "95th_quantile",
}
